import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, FormControl, FormLabel, Image, Input, Select, Text, VStack, Badge, HStack, useToast } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { VMS_COLORS } from "../constants/theme";
import { vmsApi } from "../services/vmsApi";

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const VmsBook = () => {
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const officeId = (searchParams.get("office_id") || "").trim();
  const personIdFromLink = (searchParams.get("person_id") || "").trim();
  const isHostLocked = Boolean(personIdFromLink);
  const [persons, setPersons] = useState([]);
  const [gates, setGates] = useState([]);
  const [officeName, setOfficeName] = useState("");
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [result, setResult] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [form, setForm] = useState({
    office_person_id: personIdFromLink,
    full_name: "",
    address: "",
    identification: "",
    phone_number: "",
    visit_purpose: "",
    image: "",
    email: "",
    gate_name: "",
  });

  const linkError = useMemo(() => {
    if (!officeId) return "Invalid booking link: missing office.";
    if (!uuidRegex.test(officeId)) return "Invalid booking link: office is malformed.";
    if (personIdFromLink && !uuidRegex.test(personIdFromLink)) return "Invalid booking link: host is malformed.";
    return "";
  }, [officeId, personIdFromLink]);

  useEffect(() => {
    if (linkError) return;
    const loadContext = async () => {
      setIsLoadingContext(true);
      try {
        const [personsData, gatesData, officesData] = await Promise.all([
          vmsApi.getPublicOfficePersons(officeId),
          vmsApi.getGates(),
          vmsApi.getOffices(),
        ]);
        const personList = Array.isArray(personsData?.persons) ? personsData.persons : Array.isArray(personsData) ? personsData : [];
        const officeList = Array.isArray(officesData?.offices)
          ? officesData.offices
          : Array.isArray(officesData)
            ? officesData
            : Array.isArray(officesData?.data)
              ? officesData.data
              : [];
        const matchedOffice = officeList.find((o) => String(o?.office_id || o?.id || "") === officeId);
        setOfficeName(matchedOffice?.name || "");
        setPersons(personList);
        setGates(Array.isArray(gatesData) ? gatesData : []);
        if (personIdFromLink) {
          const hasHost = personList.some((p) => String(p?.person_id || p?.id) === personIdFromLink);
          if (!hasHost) {
            toast({
              title: "Invalid link",
              description: "This host link is no longer valid. Ask your host for a new link.",
              status: "error",
            });
          }
        }
      } catch (err) {
        toast({ title: "Failed to load booking context", description: err?.message, status: "error" });
      } finally {
        setIsLoadingContext(false);
      }
    };
    loadContext().catch(() => {});
  }, [linkError, officeId, personIdFromLink, toast]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const selectedHost = persons.find((p) => String(p?.person_id || p?.id || "") === String(form.office_person_id || personIdFromLink || ""));

  const submit = async (e) => {
    e.preventDefault();
    if (linkError) return;
    const officePersonId = isHostLocked ? personIdFromLink : form.office_person_id;
    if (!officePersonId) {
      toast({ title: "Host required", description: "Please select who you want to meet.", status: "warning" });
      return;
    }
    if (!form.full_name || !form.address || !form.identification || !form.phone_number || !form.visit_purpose || !form.image) {
      toast({ title: "Missing fields", description: "Please fill all required visitor fields.", status: "warning" });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        office_id: officeId,
        office_person_id: officePersonId,
        full_name: form.full_name,
        address: form.address,
        identification: form.identification,
        phone_number: form.phone_number,
        visit_purpose: form.visit_purpose,
        image: form.image,
        ...(form.email ? { email: form.email } : {}),
        ...(form.gate_name ? { gate_name: form.gate_name } : {}),
      };
      const res = await vmsApi.visitorSignIn(payload);
      setResult(res?.request || res?.data || res || null);
      toast({ title: "Sign-in successful", status: "success" });
    } catch (err) {
      const is404 = Number(err?.status) === 404;
      toast({
        title: is404 ? "Link invalid" : "Sign-in failed",
        description: is404 ? "Link expired or office updated. Ask your host for a new link." : err?.message,
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherRequest = () => {
    setResult(null);
    setForm({
      office_person_id: personIdFromLink || "",
      full_name: "",
      address: "",
      identification: "",
      phone_number: "",
      visit_purpose: "",
      image: "",
      email: "",
      gate_name: "",
    });
  };

  const startCamera = async () => {
    try {
      setIsStartingCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      setIsCameraOpen(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      toast({ title: "Camera unavailable", description: "Allow camera access or use image upload.", status: "warning" });
    } finally {
      setIsStartingCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setForm((prev) => ({ ...prev, image: dataUrl }));
    stopCamera();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const value = typeof reader.result === "string" ? reader.result : "";
      setForm((prev) => ({ ...prev, image: value }));
    };
    reader.readAsDataURL(file);
  };

  if (linkError) {
    return (
      <Box minH="100vh" bg={VMS_COLORS.bg} p={6} display="grid" placeItems="center">
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="12px" p={6} maxW="560px" w="100%">
          <Text fontWeight="700" color="red.600">
            {linkError}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={VMS_COLORS.bg} p={6} display="grid" placeItems="center">
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="12px" p={6} maxW="760px" w="100%">
        <VStack align="stretch" spacing={4} as={result ? "div" : "form"} onSubmit={result ? undefined : submit}>
          <Text fontSize="2xl" fontWeight="800" color={VMS_COLORS.primary}>
            Visitor Booking
          </Text>
          <Text color="gray.600" fontSize="sm">
            Complete your details to request access.
          </Text>

          <HStack spacing={3} flexWrap="wrap">
            <Badge colorScheme="green">Office: {officeName || officeId}</Badge>
            {isHostLocked ? <Badge colorScheme="purple">Host locked by link</Badge> : null}
          </HStack>

          {result?.card ? (
            <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p={4}>
              <Text fontWeight="700" mb={2}>
                Pass Details
              </Text>
              <Text fontSize="sm">
                <b>Pass Code:</b> {result?.card?.pass_code || "-"}
              </Text>
              <Text fontSize="sm">
                <b>Expires At:</b> {result?.card?.expires_at || "-"}
              </Text>
            </Box>
          ) : (
            <>
              <FormControl isRequired>
                <FormLabel>Person to Meet</FormLabel>
                {isHostLocked ? (
                  <Input
                    isReadOnly
                    value={
                      selectedHost
                        ? `${selectedHost?.full_name || selectedHost?.name || "Host"}${selectedHost?.title ? ` (${selectedHost.title})` : ""}`
                        : personIdFromLink
                    }
                  />
                ) : (
                  <Select
                    value={form.office_person_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, office_person_id: e.target.value }))}
                    placeholder={isLoadingContext ? "Loading hosts..." : "Select host"}
                  >
                    {persons.map((person) => {
                      const id = String(person?.person_id || person?.id || "");
                      return (
                        <option key={id} value={id}>
                          {person?.full_name || person?.name || id}
                          {person?.title ? ` (${person.title})` : ""}
                        </option>
                      );
                    })}
                  </Select>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Full Name</FormLabel>
                <Input value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Address</FormLabel>
                <Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Identification</FormLabel>
                <Input value={form.identification} onChange={(e) => setForm((p) => ({ ...p, identification: e.target.value }))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Phone Number</FormLabel>
                <Input value={form.phone_number} onChange={(e) => setForm((p) => ({ ...p, phone_number: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Email (Optional)</FormLabel>
                <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Visit Purpose</FormLabel>
                <Input value={form.visit_purpose} onChange={(e) => setForm((p) => ({ ...p, visit_purpose: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Gate (Optional)</FormLabel>
                <Select value={form.gate_name} onChange={(e) => setForm((p) => ({ ...p, gate_name: e.target.value }))}>
                  <option value="">Select gate</option>
                  {gates.map((g) => (
                    <option key={g?.gate_id || g?.name} value={g?.name || ""}>
                      {g?.name || "-"}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Photo</FormLabel>
                <VStack align="stretch" spacing={2}>
                  {!isCameraOpen ? (
                    <HStack spacing={2}>
                      <Button type="button" variant="outline" onClick={startCamera} isLoading={isStartingCamera}>
                        Open Camera
                      </Button>
                      <Input type="file" accept="image/*" capture="user" onChange={onFilePicked} />
                    </HStack>
                  ) : null}
                  {isCameraOpen ? (
                    <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p={2}>
                      <video ref={videoRef} style={{ width: "100%", borderRadius: "8px" }} autoPlay playsInline muted />
                      <HStack mt={2}>
                        <Button type="button" colorScheme="green" onClick={capturePhoto}>
                          Capture
                        </Button>
                        <Button type="button" variant="outline" onClick={stopCamera}>
                          Cancel
                        </Button>
                      </HStack>
                    </Box>
                  ) : null}
                  {form.image ? (
                    <Box>
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        Captured image preview
                      </Text>
                      <Image src={form.image} alt="Captured visitor" maxH="220px" objectFit="contain" borderRadius="10px" />
                    </Box>
                  ) : null}
                </VStack>
                <canvas ref={canvasRef} style={{ display: "none" }} />
              </FormControl>

              <Button type="submit" colorScheme="green" isLoading={isSubmitting || isLoadingContext}>
                Submit Sign-in
              </Button>
            </>
          )}

          {result ? (
            <Button variant="outline" onClick={resetForAnotherRequest}>
              Send Another Request
            </Button>
          ) : null}
        </VStack>
      </Box>
    </Box>
  );
};

export default VmsBook;
